/**
 * ChartDataProcessor - Data Processing Service
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: This service is solely responsible for transforming raw sensor data
 * into a format suitable for chart visualization. It handles data grouping,
 * filtering, and preprocessing without any knowledge of chart rendering concerns.
 * 
 * Responsibilities:
 * - Group raw sensor data by timestamp
 * - Filter out excluded/bad data points
 * - Separate directional vs regular sensor fields
 * - Process temporal data alignment
 * 
 * Context: Part of buoy sensor data visualization system. Raw data comes from
 * InfluxDB with measurements like 'procv_co2', 'virtual_waves', etc. Each data
 * point has a timestamp, field name, value, and metadata.
 */

/**
 * Configuration for fields that represent directional data (angles/compass directions)
 * These fields require special visualization treatment (arrows instead of line graphs)
 */
const DIRECTIONAL_FIELDS = [
  'virtual_waves:dir_mean_deg',      // Wave direction in degrees
  'virtual_wind:wind_dir_avg_deg',   // Wind direction in degrees  
  'virtual_currents:dir_deg'         // Current direction in degrees
];

export class ChartDataProcessor {
  /**
   * Primary data processing method that transforms raw sensor data into chart-ready format
   * 
   * @param {Array} rawData - Array of raw data points from InfluxDB
   *   Each point: { _measurement, _field, _value, _time, buoy_id, mode?, ... }
   * @param {Array} selectedFields - Array of field keys user wants to visualize
   *   Format: ['measurement:field', 'procv_co2:pCO2', 'virtual_waves:hs_m']
   * @param {Array} badDataBin - Array of excluded data points
   *   Each exclusion: { pointKey: 'buoyId-timestamp-fieldKey' }
   * 
   * @returns {Object} Processed data structure
   *   {
   *     chartData: Array of time-grouped data points for chart rendering,
   *     directionalFields: Array of directional field keys,
   *     regularFields: Array of non-directional field keys
   *   }
   */
  static processChartData(rawData = [], selectedFields = [], badDataBin = [], mergedMode = false) {
    // Early return if no fields selected - avoid unnecessary processing
    if (!selectedFields.length) {
      return {
        chartData: [],
        directionalFields: [],
        regularFields: []
      };
    }

    // Separate field types for different rendering approaches
    const { directionalFields, regularFields } = this._categorizeFields(selectedFields, mergedMode);
    
    // Transform raw data into time-grouped structure
    const chartData = this._groupDataByTime(rawData, selectedFields, badDataBin, mergedMode);
    
    // Log processing results for debugging/monitoring
    this._logProcessingResults(chartData, selectedFields, mergedMode);
    
    return {
      chartData,
      directionalFields,
      regularFields
    };
  }

  /**
   * Categorizes selected fields into directional vs regular types
   * 
   * SIMPLIFIED: Treat all fields as regular fields for data processing and axis creation.
   * Directional fields are just marked for special arrow rendering, not separated.
   * 
   * @param {Array} selectedFields - Field keys to categorize (may be buoy-prefixed)
   * @param {boolean} mergedMode - Whether in merged mode with buoy prefixes
   * @returns {Object} { directionalFields, regularFields }
   * @private
   */
  static _categorizeFields(selectedFields, mergedMode = false) {
    const stripBuoy = (k) => mergedMode ? k.replace(/^[A-Z0-9]+_/, '') : k;

    // Find which fields are directional (for arrow rendering only)
    const directionalFields = selectedFields.filter(field => 
      DIRECTIONAL_FIELDS.includes(stripBuoy(field))
    );

    // SIMPLIFIED: Treat ALL fields as regular fields for data processing
    // This ensures directional fields get proper axes and data handling
    const regularFields = selectedFields; // All fields are regular for processing

    return { directionalFields, regularFields };
  }

  /**
   * Groups raw data points by timestamp, creating chart-ready data structure
   * 
   * Process:
   * 1. Filter data to only selected fields
   * 2. Group by rounded timestamp (to nearest minute for alignment)
   * 3. Exclude bad/filtered data points
   * 4. Mark negative values as potentially bad
   * 5. Sort chronologically
   * 
   * @param {Array} rawData - Raw sensor data points
   * @param {Array} selectedFields - Fields to include in processing
   * @param {Array} badDataBin - Excluded data points
   * @returns {Array} Time-grouped chart data
   * @private
   */
  static _groupDataByTime(rawData, selectedFields, badDataBin, mergedMode = false) {
    const grouped = {};

    rawData.forEach(dataPoint => {
      // Create field identifier from measurement and field name
      const baseFieldKey = `${dataPoint._measurement}:${dataPoint._field}`;
      
      // Skip if this field isn't selected for visualization
      if (!selectedFields.includes(baseFieldKey)) return;
      
      // Use the base field key directly - buoy distinction will be handled at chart level  
      const buoyId = dataPoint.buoy_id || dataPoint.buoyId;
      const fieldKey = baseFieldKey;
      
      const value = dataPoint._value;
      
      // Round timestamp to nearest minute for better data alignment
      // This helps group readings that are close in time
      const timeKey = new Date(dataPoint._time).toISOString().slice(0, 16);
      
      // Check if this specific data point has been manually excluded  
      const pointKey = `${buoyId}-${timeKey}-${baseFieldKey}`;
      const isExcluded = badDataBin.some(bad => bad.pointKey === pointKey);
      
      if (isExcluded) return; // Skip excluded data points

      // Initialize time group if it doesn't exist
      if (!grouped[timeKey]) {
        grouped[timeKey] = { time: timeKey };
      }

      // In merged mode, we need to handle multiple buoys having the same field
      // Create a buoy-specific field key for storage but keep original for processing
      if (mergedMode) {
        const buoyFieldKey = `${buoyId}_${fieldKey}`;
        grouped[timeKey][buoyFieldKey] = value;
        
        // Also store buoy info for this field
        grouped[timeKey][`${buoyFieldKey}_buoyId`] = buoyId;
        
        // Special handling for ProCV CO2 sensor mode information
        if (dataPoint.mode && (fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2')) {
          grouped[timeKey][`${buoyFieldKey}_mode`] = dataPoint.mode;
        }
        
        // Flag potentially bad data using sensor-specific thresholds
        if (this._isSuspiciousValue(value, baseFieldKey, dataPoint)) {
          grouped[timeKey][`${buoyFieldKey}_isBad`] = true;
        }
      } else {
        // Single buoy mode - use original logic
        grouped[timeKey][fieldKey] = value;
        
        // Special handling for ProCV CO2 sensor mode information
        if (dataPoint.mode && (fieldKey === 'procv_co2:pCO2' || fieldKey === 'mini_co2:pCO2')) {
          grouped[timeKey][`${fieldKey}_mode`] = dataPoint.mode;
        }
        
        // Flag potentially bad data using sensor-specific thresholds
        if (this._isSuspiciousValue(value, baseFieldKey, dataPoint)) {
          grouped[timeKey][`${fieldKey}_isBad`] = true;
        }
      }
    });

    // Convert grouped object to sorted array for chart consumption
    return Object.values(grouped).sort((a, b) => 
      new Date(a.time) - new Date(b.time)
    );
  }

  /**
   * Determines if a data value is suspicious and should be flagged as potentially bad
   * 
   * @param {number} value - The data value to check
   * @param {string} fieldKey - Field identifier (measurement:field format)
   * @param {Object} dataPoint - Complete data point with metadata
   * @returns {boolean} True if value is suspicious
   * @private
   */
  static _isSuspiciousValue(value, fieldKey, dataPoint) {
    if (typeof value !== 'number' || !isFinite(value)) return false;

    const [measurement, field] = fieldKey.split(':');
    
    // Virtual sensors are exempt from general negative value checks
    // They may legitimately have negative values (temperatures, tide levels, etc.)
    const isVirtualSensor = measurement.startsWith('virtual_');
    
    // Non-virtual sensors: check for negative values (most physical sensors shouldn't be negative)
    if (!isVirtualSensor && value < 0) {
      // Allow some specific fields that can legitimately be negative
      const allowsNegative = [
        'air_temp_c',
        'sea_surface_temp_c', 
        'temperature_c',
        'water_level_m',
        'prediction_m'
      ];
      
      if (!allowsNegative.some(allowed => field.includes(allowed))) {
        return true;
      }
    }

    // Sensor-specific suspicious value ranges
    // 
    // CONFIGURATION: Only PCO2 currently has thresholds active
    // To add new sensor thresholds, uncomment and configure the appropriate lines below:
    //
    const suspiciousRanges = {
      'pCO2': { min: 200, max: 2000 },  // Typical seawater pCO2 range in μatm
      
      // Future sensor thresholds - uncomment and adjust ranges as needed:
      // 'salinity_psu': { min: 0, max: 40 },        // Typical seawater salinity
      // 'oxygen_ml_per_l': { min: 0, max: 15 },     // Typical dissolved oxygen
      // 'temperature_c': { min: -5, max: 35 },      // Typical water temperature
      // 'conductivity': { min: 0, max: 60 },        // Typical conductivity range
      // 'pressure': { min: 0, max: 50 },            // Typical pressure range
    };

    // Check field-specific ranges
    for (const [fieldPattern, range] of Object.entries(suspiciousRanges)) {
      if (field.includes(fieldPattern)) {
        if (value < range.min || value > range.max) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Logs processing results for debugging and monitoring
   * Helps developers understand data flow and troubleshoot issues
   * 
   * @param {Array} processedData - Final processed chart data
   * @param {Array} selectedFields - Fields that were requested
   * @private
   */
  static _logProcessingResults(processedData, selectedFields, mergedMode = false) {
    console.log('ChartDataProcessor Results:', {
      selectedFields,
      dataPoints: processedData.length,
      fieldKeys: Object.keys(processedData[0] || {}).filter(k => k !== 'time'),
      sampleData: processedData.slice(0, 2),
      mergedMode,
      
      // Special debugging for pCO2 sensor (known to have issues)
      pCO2Debug: (selectedFields.includes('procv_co2:pCO2') || selectedFields.includes('mini_co2:pCO2')) ? {
        procvValues: processedData.slice(0, 5)
          .map(d => d['procv_co2:pCO2'])
          .filter(v => v !== undefined),
        miniValues: processedData.slice(0, 5)
          .map(d => d['mini_co2:pCO2'])
          .filter(v => v !== undefined),
        totalProcvPoints: processedData
          .map(d => d['procv_co2:pCO2'])
          .filter(v => v !== undefined).length,
        totalMiniPoints: processedData
          .map(d => d['mini_co2:pCO2'])
          .filter(v => v !== undefined).length
      } : null
    });
  }

  /**
   * Filters processed chart data by time range (used for zoom functionality)
   * 
   * @param {Array} chartData - Full chart data array
   * @param {string|Date} startTime - Start of time range
   * @param {string|Date} endTime - End of time range
   * @returns {Array} Filtered chart data within time range
   */
  static filterByTimeRange(chartData, startTime, endTime) {
    const startTimestamp = new Date(startTime).getTime();
    const endTimestamp = new Date(endTime).getTime();
    
    return chartData.filter(item => {
      const itemTimestamp = new Date(item.time).getTime();
      return itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp;
    });
  }

  /**
   * Processes chart data for merged mode with per-buoy field selections
   * 
   * @param {Array} rawData - Array of raw data points from InfluxDB
   * @param {Object} perBuoySelectedFields - Object mapping buoyId to selected fields
   * @param {Array} selectedBuoys - Array of selected buoy IDs
   * @param {Array} badDataBin - Array of excluded data points
   * @returns {Object} Processed data structure with merged buoy data
   */
  static processChartDataMerged(rawData, perBuoySelectedFields, selectedBuoys, badDataBin = []) {
    const allSelectedFields = new Set();
    const buoyFieldMapping = {};
    
    // Create field mappings and collect all unique fields
    selectedBuoys.forEach(buoyId => {
      const fields = perBuoySelectedFields[buoyId] || [];
      fields.forEach(field => {
        allSelectedFields.add(field);
        const buoyFieldKey = `${buoyId}_${field}`;
        buoyFieldMapping[buoyFieldKey] = { buoyId, originalField: field };
      });
    });
    
    // Filter and process raw data
    const filteredData = rawData.filter(d => {
      const buoyId = d.buoy_id || d.buoyId;
      if (!selectedBuoys.includes(buoyId)) return false;
      
      const baseFieldKey = `${d._measurement}:${d._field}`;
      const fieldsForThisBuoy = perBuoySelectedFields[buoyId] || [];
      return fieldsForThisBuoy.includes(baseFieldKey);
    });
    
    // Process with merged mode enabled
    const processed = this.processChartData(filteredData, [...allSelectedFields], badDataBin, true);
    
    return processed;
  }

  /**
   * Gets the list of directional fields that require arrow visualization
   * 
   * @returns {Array} Array of directional field identifiers
   */
  static getDirectionalFields() {
    return [...DIRECTIONAL_FIELDS];
  }

  /**
   * Public method to categorize fields into directional vs regular
   * 
   * @param {Array} selectedFields - Field keys to categorize
   * @param {boolean} mergedMode - Whether in merged mode with buoy prefixes
   * @returns {Object} { directionalFields, regularFields }
   */
  static categorizeFields(selectedFields, mergedMode = false) {
    return this._categorizeFields(selectedFields, mergedMode);
  }
}

export default ChartDataProcessor;